/* ──────────────────────────────────────────────────────────────
   SessionContext.tsx · the real signed-in user/org from platform /v1/me
   Replaces all hardcoded "Priya Raman / Madras Filter Co." mock identity.
   ────────────────────────────────────────────────────────────── */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getCurrentUser } from '../services/auth/auth';

export interface SessionUser {
  id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
}
export interface SessionOrg {
  id?: string;
  name?: string;
}

interface SessionValue {
  user: SessionUser | null;
  org: SessionOrg | null;
  role: string | null;
  loading: boolean;
  fullName: string;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue>({
  user: null, org: null, role: null, loading: true, fullName: '', refresh: async () => {},
});

// Maps the platform role slug to a human label for the sidebar.
function roleLabel(role: string | null): string {
  switch (role) {
    case 'org_admin': return 'Owner';
    case 'admin': return 'Admin';
    case 'platform_admin': return 'Platform Admin';
    case 'user': return 'Member';
    default: return role ? role.replace(/_/g, ' ') : '';
  }
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [org, setOrg] = useState<SessionOrg | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await getCurrentUser();
      const d = res?.data ?? res;
      setUser(d?.user ?? null);
      setOrg(d?.org ?? null);
      setRole(d?.memberships?.[0]?.role ?? null);
    } catch {
      setUser(null); setOrg(null); setRole(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();

  return (
    <SessionContext.Provider value={{ user, org, role, loading, fullName, refresh }}>
      {children}
    </SessionContext.Provider>
  );
}

export const useSession = () => useContext(SessionContext);
export { roleLabel };
